import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { TextField, FormError, SubmitButton } from '@/components/auth/FormFields';
import { authApi, readApiError } from '@/lib/auth-api';

/**
 * Login second-factor step. Reached after a password-correct login on an
 * MFA-enabled account (which sets the short-lived challenge cookie). A valid
 * TOTP — or a single-use recovery code — exchanges the challenge for a session.
 */
export function MfaChallengePage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setPending(true);
    try {
      await authApi.verifyMfa(useRecovery ? { recoveryCode: code.trim() } : { code: code.trim() });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      nav('/app', { replace: true });
    } catch (e) {
      setErr(readApiError(e, t('auth.mfa_invalid', { defaultValue: 'Invalid code — try again' })));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout title={t('auth.mfa_title', { defaultValue: 'Two-factor authentication' })} subtitle={t('auth.mfa_subtitle', { defaultValue: 'Enter the 6-digit code from your authenticator app.' })}>
      <form onSubmit={onSubmit} className="space-y-4">
        <FormError message={err} />
        <TextField
          id="mfa-code"
          label={useRecovery ? t('auth.mfa_recovery_label', { defaultValue: 'Recovery code' }) : t('auth.mfa_code_label', { defaultValue: 'Authentication code' })}
          value={code}
          onChange={setCode}
          autoFocus
          placeholder={useRecovery ? 'xxxxxxxxxx' : '123456'}
        />
        <SubmitButton pending={pending}>{t('auth.mfa_verify', { defaultValue: 'Verify' })}</SubmitButton>
        <button type="button" onClick={() => { setUseRecovery((v) => !v); setCode(''); setErr(null); }} className="w-full text-center text-sm text-ink-500 hover:text-ink-800">
          {useRecovery ? t('auth.mfa_use_code', { defaultValue: 'Use your authenticator app instead' }) : t('auth.mfa_use_recovery', { defaultValue: 'Use a recovery code instead' })}
        </button>
      </form>
    </AuthLayout>
  );
}
